import { useState, useEffect } from "react";
import { useComponentTranslation } from "@/services/i18n";
import toast from "react-hot-toast";
import api from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/loader";
import { FiMessageCircle, FiSave } from "react-icons/fi";

const translations = {
  en: {
    title: "WhatsApp Configuration",
    description: "Configure WhatsApp for candidate communication",
    phone_number: "WhatsApp Business Number",
    phone_number_placeholder: "+1234567890",
    phone_number_help: "Enter your WhatsApp Business number with country code",
    api_key: "API Key",
    api_key_placeholder: "Enter your WhatsApp API key",
    api_key_help: "API key from your WhatsApp Business provider",
    enabled: "Enable WhatsApp Communication",
    enabled_help: "Allow sending messages to candidates via WhatsApp",
    save: "Save Configuration",
    saving: "Saving...",
    success: "WhatsApp configuration saved successfully",
    error: "Error saving WhatsApp configuration",
    fetch_error: "Error fetching WhatsApp configuration",
  },
  fr: {
    title: "Configuration WhatsApp",
    description: "Configurer WhatsApp pour la communication avec les candidats",
    phone_number: "Numéro WhatsApp Business",
    phone_number_placeholder: "+33123456789",
    phone_number_help: "Entrez votre numéro WhatsApp Business avec l'indicatif pays",
    api_key: "Clé API",
    api_key_placeholder: "Entrez votre clé API WhatsApp",
    api_key_help: "Clé API de votre fournisseur WhatsApp Business",
    enabled: "Activer la communication WhatsApp",
    enabled_help: "Autoriser l'envoi de messages aux candidats via WhatsApp",
    save: "Enregistrer la configuration",
    saving: "Enregistrement...",
    success: "Configuration WhatsApp enregistrée avec succès",
    error: "Erreur lors de l'enregistrement de la configuration WhatsApp",
    fetch_error: "Erreur lors de la récupération de la configuration WhatsApp",
  },
};

export default function WhatsApp() {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({
    whatsapp_enabled: false,
    whatsapp_phone_number: "",
    whatsapp_api_key: "",
  });

  useEffect(() => {
    fetchConfiguration();
  }, [organization?._id]);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/organization/me", { organization_id: organization?._id });
      if (ok && data) {
        setValues({
          whatsapp_enabled: data.whatsapp_enabled || false,
          whatsapp_phone_number: data.whatsapp_phone_number || "",
          whatsapp_api_key: data.whatsapp_api_key || "",
        });
      }
    } catch (e) {
      console.error("Error fetching WhatsApp configuration:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await api.put("/organization", { ...values, organization_id: organization._id });
      if (response.ok) {
        toast.success(t("success"));
      } else {
        toast.error(response.code || "Error saving configuration");
      }
    } catch (e) {
      console.error("Error saving WhatsApp configuration:", e);
      toast.error(e.code || e.message || "Error saving configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-3xl">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <FiMessageCircle className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
            <p className="text-sm text-gray-600 mt-1">{t("description")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Enable WhatsApp */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="whatsapp_enabled"
                name="whatsapp_enabled"
                type="checkbox"
                checked={values.whatsapp_enabled}
                onChange={(e) => setValues({ ...values, whatsapp_enabled: e.target.checked })}
                className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="whatsapp_enabled" className="input-label mb-0">
                {t("enabled")}
              </label>
              <p className="text-sm text-gray-500 mt-1">{t("enabled_help")}</p>
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label htmlFor="whatsapp_phone_number" className="input-label">
              {t("phone_number")}
            </label>
            <input
              type="tel"
              id="whatsapp_phone_number"
              name="whatsapp_phone_number"
              value={values.whatsapp_phone_number}
              onChange={(e) => setValues({ ...values, whatsapp_phone_number: e.target.value })}
              placeholder={t("phone_number_placeholder")}
              className="input"
            />
            <p className="text-sm text-gray-500 mt-1">{t("phone_number_help")}</p>
          </div>

          {/* API Key */}
          <div>
            <label htmlFor="whatsapp_api_key" className="input-label">
              {t("api_key")}
            </label>
            <input
              type="password"
              id="whatsapp_api_key"
              name="whatsapp_api_key"
              value={values.whatsapp_api_key}
              onChange={(e) => setValues({ ...values, whatsapp_api_key: e.target.value })}
              placeholder={t("api_key_placeholder")}
              className="input"
            />
            <p className="text-sm text-gray-500 mt-1">{t("api_key_help")}</p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end border-t border-gray-200 pt-6">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <Loader size="small" color="white" />
                  <span>{t("saving")}</span>
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  <span>{t("save")}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}























